import { autoinject, computedFrom, PLATFORM } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

import {
	BaseDialog,
	BaseDialogModel,
	BaseDialogModelDefaults,
	DialogSize,
} from '~/components/dialog/base-dialog';

export enum SimpleDialogType {
	ERROR,
	INFO,
	SUCCESS,
	WARNING,
}

export enum SimpleDialogButtons {
	OK,
	CANCEL,
}

export interface SimpleDialogModel extends BaseDialogModel {
	buttons?: SimpleDialogButtons[];
	simpleDialogType?: SimpleDialogType;
}

export const SimpleDialogModelDefaults: SimpleDialogModel = {
	...BaseDialogModelDefaults,
	buttons: [SimpleDialogButtons.OK, SimpleDialogButtons.CANCEL],
	dialogSize: DialogSize.SMALL,
	scrollable: false,
	simpleDialogType: SimpleDialogType.INFO,
};

/**
 * See https://aurelia.io/docs/plugins/dialog
 */
@autoinject
export class SimpleDialog<
	DialogModel extends SimpleDialogModel,
> extends BaseDialog<DialogModel> {
	public static HeadingText: { [key in SimpleDialogType]: string } = {
		[SimpleDialogType.INFO]: 'notifications.info',
		[SimpleDialogType.SUCCESS]: 'notifications.success',
		[SimpleDialogType.WARNING]: 'notifications.warning',
		[SimpleDialogType.ERROR]: 'notifications.error',
	};

	/**
	 * The dialog view model
	 */
	protected model: DialogModel;

	constructor(protected readonly i18n?: I18N, ...rest) {
		super(...rest);
		PLATFORM.moduleName('./simple-dialog');
	}

	protected activate(
		model: DialogModel,
		defaults: DialogModel = SimpleDialogModelDefaults as DialogModel,
	): Promise<DialogModel> {
		return super.activate(model, defaults).then(() => {
			if (
				this.model.headingText === null ||
				this.model.headingText === undefined
			) {
				this.model.headingText =
					SimpleDialog.HeadingText[this.model.simpleDialogType];
			}
			return this.model;
		});
	}

	@computedFrom('model.buttons')
	get hasOkButton(): boolean {
		return (
			this.model.buttons &&
			this.model.buttons.indexOf(SimpleDialogButtons.OK) >= 0
		);
	}

	@computedFrom('model.buttons')
	get hasCancelButton(): boolean {
		return (
			this.model.buttons &&
			this.model.buttons.indexOf(SimpleDialogButtons.CANCEL) >= 0
		);
	}
}
